// ============================================================================
//  Roster-driven NPC spawner
// ============================================================================
// characters.js is the canonical cast. This module turns character DATA into
// live Townsperson instances with the right mesh, behavior, and schedule.
// Adding a new NPC means adding an entry to characters.js — no scene code.

import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, mat } from '../core/meshkit.js';
import { Townsperson, breadSlice, stickLegs } from './townsfolk.js';
import { townsfolk, customers, shopkeeper, spawnable } from './characters.js';

const CHAR = 0x2E2018;
const EYE_W = 0xFFFFFF;

// ---- per-character visual builders ---------------------------------------
// Most characters are built generically from their `kind` + `colors`. A small
// set of distinctive characters get custom builders so they remain recognizable.

function torus(r, tube, color, x, y, z, g, seg = 16){
  const m = new THREE.Mesh(new THREE.TorusGeometry(r, tube, 8, seg), mat(color));
  m.position.set(x, y, z);
  m.castShadow = true;
  g.add(m);
  return m;
}

function buildBread(c){
  return (g) => {
    breadSlice(g, c.colors.crust || C.crust, c.colors.body || C.toast, 0.6, 1.1);
    sphere(.06, CHAR, -.15, 1.15, .3, g, 8);
    sphere(.06, CHAR,  .15, 1.15, .3, g, 8);
    stickLegs(g, .48);
  };
}

function buildFruit(c){
  return (g) => {
    sphere(.55, c.colors.body || C.sprout, 0, .85, 0, g, 16);
    sphere(.06, CHAR, -.15, .95, .45, g, 8);
    sphere(.06, CHAR,  .15, .95, .45, g, 8);
    if (c.colors.leaf){
      const leaf = box(.22, .06, .42, c.colors.leaf, 0, 1.32, 0, g);
      leaf.rotation.x = -.3;
    }
    if (c.colors.stem) cyl(.04, .04, .3, c.colors.stem, 0, 1.48, 0, g, 6);
    stickLegs(g, .45);
  };
}

function buildFood(c){
  return (g) => {
    box(.7, .7, .7, c.colors.body || C.cream, 0, .8, 0, g);
    sphere(.06, CHAR, -.15, 1.05, .38, g, 8);
    sphere(.06, CHAR,  .15, 1.05, .38, g, 8);
    stickLegs(g, .42);
  };
}

function buildCreature(c){
  return (g) => {
    sphere(.5, c.colors.body || C.cream, 0, .9, 0, g, 14);
    sphere(.06, CHAR, -.15, 1.0, .38, g, 8);
    sphere(.06, CHAR,  .15, 1.0, .38, g, 8);
    stickLegs(g, .45);
  };
}

function buildHuman(c){
  return (g) => {
    cyl(.25, .25, .9, c.colors.skin || C.cream, 0, .9, 0, g, 12);
    sphere(.28, c.colors.skin || C.cream, 0, 1.45, 0, g, 14);
    sphere(.05, CHAR, -.1, 1.5, .22, g, 8);
    sphere(.05, CHAR,  .1, 1.5, .22, g, 8);
    stickLegs(g, .5);
  };
}

// ---- distinctive character builders --------------------------------------

const SPECIAL = {
  sheriffberry: (c) => (g) => {
    const red = c.colors.body || 0xE23B4E;
    sphere(.55, red, 0, .85, 0, g, 16);
    for (let i = 0; i < 9; i++){
      const a = i / 9 * Math.PI * 2;
      sphere(.035, 0xF7E39B, Math.cos(a) * .52, .85 + Math.sin(i * 1.7) * .28, Math.sin(a) * .48, g, 6);
    }
    for (let i = 0; i < 5; i++){
      const a = i / 5 * Math.PI * 2;
      const leaf = box(.16, .06, .34, c.colors.leaf || C.sprout, Math.cos(a) * .16, 1.34, Math.sin(a) * .16, g);
      leaf.rotation.y = -a; leaf.rotation.x = -.35;
    }
    const hat = new THREE.Group(); hat.position.set(0, 1.42, 0); hat.rotation.z = .16; g.add(hat);
    cyl(.62, .62, .06, c.colors.hat || 0xE8D6AE, 0, 0, 0, hat, 18);
    cyl(.34, .4, .42, c.colors.hat || 0xE8D6AE, 0, .24, 0, hat, 18);
    cyl(.42, .42, .07, 0x8A5A34, 0, .06, 0, hat, 18);
    sphere(.055, CHAR, -.17, .95, .5, g, 8);
    sphere(.055, CHAR,  .17, .95, .5, g, 8);
    box(.5, .12, .08, 0x5A3A22, 0, .5, .52, g);
    stickLegs(g, .55);
  },

  bullnana: (c) => (g) => {
    const y = c.colors.body || 0xF6D336;
    box(1.5, .5, .62, y, 0, .78, 0, g);
    box(.55, .46, .58, y, .78, .64, 0, g);
    box(.55, .46, .58, y, -.78, .64, 0, g);
    box(.16, .16, .16, c.colors.tips || C.crustDark,  1.02, .64, 0, g);
    box(.16, .16, .16, c.colors.tips || C.crustDark, -1.02, .64, 0, g);
    box(.52, .52, .52, y, .98, 1.0, 0, g);
    const h1 = cyl(.02, .09, .32, C.cream, .86, 1.35, .2, g, 6); h1.rotation.z = .5;
    const h2 = cyl(.02, .09, .32, C.cream, .86, 1.35, -.2, g, 6); h2.rotation.z = .5;
    const ring = torus(.09, .022, 0xE87BA0, 1.26, .88, 0, g); ring.rotation.y = Math.PI / 2;
    sphere(.055, CHAR, 1.18, 1.1, .17, g, 8);
    sphere(.055, CHAR, 1.18, 1.1, -.17, g, 8);
    [[-.6, .34], [.6, .34], [-.6, -.34], [.6, -.34]]
      .forEach(([x, z]) => cyl(.1, .1, .58, C.rye, x, .28, z, g, 6));
  },

  snailman: (c) => (g) => {
    const skin = c.colors.body || C.sproutDim;
    box(.95, .26, .46, skin, 0, .17, 0, g);
    box(.36, .34, .42, skin, .52, .32, 0, g);
    const shell = new THREE.Group(); shell.position.set(-.22, .58, 0); g.add(shell);
    cyl(.42, .42, .3, c.colors.shell || C.rye, 0, 0, 0, shell, 18);
    cyl(.3, .3, .2, 0x9A6A3C, 0, .22, 0, shell, 16);
    cyl(.16, .16, .14, C.crust, 0, .38, 0, shell, 14);
    cyl(.03, .03, .3, skin, .64, .55, .12, g, 6); sphere(.06, C.cream, .64, .72, .12, g, 8);
    cyl(.03, .03, .3, skin, .64, .55, -.12, g, 6); sphere(.06, C.cream, .64, .72, -.12, g, 8);
    sphere(.04, CHAR, .68, .38, .1, g, 8);
    sphere(.04, CHAR, .68, .38, -.1, g, 8);
  },

  footman: (c) => (g) => {
    const skin = c.colors.body || 0xF0C89A;
    box(.58, .8, 1.0, skin, 0, .7, .08, g);
    for (let i = 0; i < 5; i++) box(.09, .13, .16, skin, -.22 + i * .11, .28, .62, g);
    const b1 = box(.17, .04, .03, c.colors.brows || C.cocoa, -.14, 1.0, .5, g); b1.rotation.z =  .45;
    const b2 = box(.17, .04, .03, c.colors.brows || C.cocoa,  .14, 1.0, .5, g); b2.rotation.z = -.45;
    sphere(.06, CHAR, -.14, .9, .5, g, 8);
    sphere(.06, CHAR,  .14, .9, .5, g, 8);
    box(.1, .34, .03, C.cobalt, 0, .55, .5, g);
  },

  kingbread: (c) => (g) => {
    const SPIRIT = c.colors.robe || 0xE8C24B;
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.34, 14, 14), new THREE.MeshLambertMaterial({ color: SPIRIT, transparent: true, opacity: 0.9 }));
    body.position.y = 1.05; g.add(body);
    box(0.65, 0.75, 0.3, SPIRIT, 0, 0.52, 0, g);
    sphere(0.05, c.colors.body || 0xFFF4DF, -0.13, 1.12, 0.22, g, 8);
    sphere(0.05, c.colors.body || 0xFFF4DF,  0.13, 1.12, 0.22, g, 8);
    const crown = new THREE.Group(); crown.position.set(0, 1.48, 0); g.add(crown);
    cyl(0.24, 0.26, 0.14, c.colors.crown || 0xFFD700, 0, 0, 0, crown, 12);
    for (let i = 0; i < 5; i++){
      const a = (i - 2) * 0.35;
      const p = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.16, 6), new THREE.MeshBasicMaterial({ color: c.colors.crown || 0xFFD700 }));
      p.position.set(Math.sin(a) * 0.22, 0.14, Math.cos(a) * 0.22); crown.add(p);
    }
    cyl(0.03, 0.03, 1.4, c.colors.crown || 0xFFD700, 0.4, 0.85, 0.2, g, 6).rotation.z = -0.2;
    sphere(0.08, c.colors.crown || 0xFFD700, 0.52, 1.55, 0.32, g, 10);
  },

  crsr: (c) => (g) => {
    breadSlice(g, c.colors.crust || C.crust, c.colors.body || C.toast, .6, 1.1);
    sphere(.07, CHAR, -.17, 1.16, .3, g, 8);
    sphere(.07, CHAR,  .17, 1.16, .3, g, 8);
    const cap = new THREE.Group(); cap.position.set(0, 1.78, 0); g.add(cap);
    cyl(.42, .46, .32, c.colors.cap || 0x1FB6A6, 0, 0, 0, cap, 18);
    box(.5, .06, .42, 0x0E8C80, 0, -.12, .34, cap);
    const chain = torus(.28, .045, c.colors.chain || 0xE8C24B, 0, .74, .32, g); chain.rotation.x = 1.25;
    box(.22, .17, .05, c.colors.chain || 0xE8C24B, 0, .52, .5, g);
    stickLegs(g, .5);
  },

  beatnik: (c) => (g) => {
    breadSlice(g, c.colors.crust || C.crust, c.colors.body || 0xC9A876, .6, 1.1);
    sphere(.06, CHAR, -.15, 1.16, .3, g, 8);
    sphere(.06, CHAR,  .15, 1.16, .3, g, 8);
    const goatee = box(.08, .12, .04, CHAR, 0, .82, .34, g); goatee.rotation.x = .2;
    cyl(.36, .36, .1, c.colors.beret || 0x3D2E8C, 0, 1.62, .02, g, 16);
    cyl(.04, .04, .07, C.crustDark, 0, 1.7, .02, g, 6);
    stickLegs(g, .48);
  },

  coffeerocker: (c) => (g) => {
    breadSlice(g, c.colors.crust || C.crust, c.colors.body || 0xC9A876, .6, 1.1);
    sphere(.06, CHAR, -.15, 1.16, .3, g, 8);
    sphere(.06, CHAR,  .15, 1.16, .3, g, 8);
    // spiky hair
    for (let i = 0; i < 5; i++){
      const a = (i - 2) * .35;
      const spike = new THREE.Mesh(new THREE.ConeGeometry(.06, .22, 6), new THREE.MeshLambertMaterial({ color: c.colors.hair || 0x6E4526 }));
      spike.position.set(Math.sin(a) * .18, 1.72, .1); g.add(spike);
    }
    // shades
    box(.32, .08, .04, 0x111111, -.12, 1.18, .34, g);
    box(.32, .08, .04, 0x111111,  .12, 1.18, .34, g);
    stickLegs(g, .48);
  },

  bananabunch: (c) => (g) => {
    const y = c.colors.body || 0xF2D23B;
    box(.6, .9, .5, y, 0, .9, 0, g);
    box(.16, .16, .16, c.colors.tips || C.crustDark, 0, 1.42, 0, g);
    sphere(.06, CHAR, -.14, 1.05, .3, g, 8);
    sphere(.06, CHAR,  .14, 1.05, .3, g, 8);
    // little bunch buddies
    sphere(.18, y, -.35, 1.25, .1, g, 8);
    sphere(.16, y, -.45, .95, .05, g, 8);
    box(.08, .08, .08, c.colors.tips || C.crustDark, -.38, 1.38, .12, g);
    stickLegs(g, .42);
  },

  ricebowl: (c) => (g) => {
    cyl(.5, .32, .42, c.colors.bowl || C.cobalt, 0, .42, 0, g, 20);
    cyl(.48, .48, .06, C.cobaltDim, 0, .63, 0, g, 20);
    sphere(.44, c.colors.body || C.cream, 0, .76, 0, g, 16);
    sphere(.12, c.colors.body || C.cream, 0, 1.12, 0, g, 10);
    cyl(.03, .03, .16, C.crustDark, 0, 1.24, 0, g, 6);
    const e1 = box(.13, .035, .03, CHAR, -.15, .86, .38, g); e1.rotation.z = -.4;
    const e2 = box(.13, .035, .03, CHAR,  .15, .86, .38, g); e2.rotation.z =  .4;
    cyl(.04, .04, .36, C.cream, -.52, .58, .12, g, 6);
    cyl(.04, .04, .36, C.cream,  .52, .58, .12, g, 6);
    stickLegs(g, .42, .24, C.cream);
  },

  gary: (c) => (g) => {
    const body = c.colors.body || 0xE8557F;
    sphere(.28, body, 0, .7, 0, g, 14);
    cyl(.12, .14, .45, body, 0, .36, 0, g, 12);
    cyl(.04, .04, .32, body, -.22, .52, .1, g, 6);
    cyl(.04, .04, .32, body,  .22, .52, .1, g, 6);
    cyl(.06, .04, .42, body, -.1, .12, 0, g, 6);
    cyl(.06, .04, .42, body,  .1, .12, 0, g, 6);
    sphere(.09, EYE_W, -.1, .78, .22, g, 10);
    sphere(.09, EYE_W,  .1, .78, .22, g, 10);
    sphere(.04, CHAR, -.1, .78, .28, g, 8);
    sphere(.04, CHAR,  .1, .78, .28, g, 8);
    cyl(.015, .015, .25, body, -.08, 1.05, 0, g, 6);
    cyl(.015, .015, .25, body,  .08, 1.05, 0, g, 6);
    sphere(.04, 0xB88FC4, -.08, 1.18, 0, g, 8);
    sphere(.04, 0xB88FC4,  .08, 1.18, 0, g, 8);
  },

  spacedogs: (c) => (g) => {
    const col = c.colors.body || 0x8C6239;
    box(0.7, 0.6, 0.9, col, 0, 0.7, 0, g);
    box(0.44, 0.44, 0.44, col, 0, 1.05, 0.45, g);
    box(0.2, 0.16, 0.22, 0x6E4A28, 0, 0.95, 0.72, g);
    sphere(0.05, CHAR, -0.1, 1.12, 0.66, g, 8); sphere(0.05, CHAR, 0.1, 1.12, 0.66, g, 8);
    const e1 = box(0.12, 0.3, 0.05, col, -0.2, 1.12, 0.35, g); e1.rotation.z = 0.4;
    const e2 = box(0.12, 0.3, 0.05, col, 0.2, 1.12, 0.35, g); e2.rotation.z = -0.4;
    box(0.5, 0.18, 0.5, c.colors.bandana || 0x1F8F5A, 0, 0.9, 0.35, g);
    for (const x of [-0.24, 0.24]) for (const z of [-0.28, 0.28]) cyl(0.09, 0.09, 0.5, 0x5A3A22, x, 0.25, z, g, 6);
  },

  slice: (c) => (g) => {
    const col = c.colors.body || 0x8C6239;
    box(0.72, 0.62, 0.92, col, 0, 0.72, 0, g);
    box(0.46, 0.46, 0.46, col, 0, 1.12, 0.46, g);
    box(0.2, 0.16, 0.22, 0x4A3020, 0, 1.0, 0.74, g);
    sphere(0.055, CHAR, -0.11, 1.18, 0.68, g, 8); sphere(0.055, CHAR, 0.11, 1.18, 0.68, g, 8);
    box(0.55, 0.2, 0.55, c.colors.bandana || 0xE23B4E, 0, 0.95, 0.36, g);
    // captain hat
    cyl(.28, .28, .1, 0x111111, 0, 1.52, 0, g, 16);
    box(.32, .2, .32, 0x111111, 0, 1.64, 0, g);
    for (const x of [-0.26, 0.26]) for (const z of [-0.3, 0.3]) cyl(0.1, 0.1, 0.52, 0x4A3020, x, 0.26, z, g, 6);
    stickLegs(g, .42);
  },

  yango: (c) => (g) => {
    const col = c.colors.body || 0x6E4A28;
    box(0.85, 0.7, 0.75, col, 0, 0.85, 0, g);
    box(0.46, 0.46, 0.46, col, 0, 1.28, 0.4, g);
    box(0.2, 0.16, 0.22, 0x4A3020, 0, 1.16, 0.66, g);
    sphere(0.05, CHAR, -0.1, 1.32, 0.6, g, 8); sphere(0.05, CHAR, 0.1, 1.32, 0.6, g, 8);
    box(0.55, 0.18, 0.55, c.colors.bandana || 0x3D5FC0, 0, 1.0, 0.34, g);
    for (const x of [-0.3, 0.3]) for (const z of [-0.28, 0.28]) cyl(0.11, 0.11, 0.56, 0x4A3020, x, 0.28, z, g, 6);
    // big arms
    cyl(.09, .09, .55, col, -.52, .95, .1, g, 8).rotation.z = .5;
    cyl(.09, .09, .55, col,  .52, .95, .1, g, 8).rotation.z = -.5;
    stickLegs(g, .45);
  },

  chawar: (c) => (g) => {
    const col = c.colors.body || 0xB07A44;
    box(0.6, 0.5, 0.7, col, 0, 0.62, 0, g);
    box(0.4, 0.36, 0.36, col, 0, 1.0, 0.36, g);
    box(0.16, 0.12, 0.18, 0x4A3020, 0, 0.9, 0.56, g);
    sphere(0.045, CHAR, -0.09, 1.08, 0.52, g, 8); sphere(0.045, CHAR, 0.09, 1.08, 0.52, g, 8);
    box(0.45, 0.12, 0.45, c.colors.bandana || 0xF7C948, 0, 0.82, 0.28, g);
    // worried eyebrows
    const b1 = box(.12, .03, .03, CHAR, -.1, 1.12, .56, g); b1.rotation.z = .3;
    const b2 = box(.12, .03, .03, CHAR,  .1, 1.12, .56, g); b2.rotation.z = -.3;
    for (const x of [-0.22, 0.22]) for (const z of [-0.22, 0.22]) cyl(0.08, 0.08, 0.38, 0x5A3A22, x, 0.19, z, g, 6);
    stickLegs(g, .38);
  },

  jessica: (c) => (g) => {
    const col = c.colors.body || 0x88CCFF;
    // holographic base
    cyl(.12, .12, .05, 0x88CCFF, 0, .02, 0, g, 12).material = new THREE.MeshBasicMaterial({ color: 0x88CCFF, transparent: true, opacity: .6 });
    // hologram body
    const body = new THREE.Mesh(new THREE.ConeGeometry(.28, .9, 12), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: .45 }));
    body.position.set(0, .55, 0); g.add(body);
    // face
    sphere(.04, 0xFFFFFF, -.08, .7, .18, g, 8); sphere(.04, 0xFFFFFF, .08, .7, .18, g, 8);
    sphere(.02, 0x2E2018, -.08, .7, .22, g, 6); sphere(.02, 0x2E2018, .08, .7, .22, g, 6);
  },

  tito: (c) => (g) => {
    const col = c.colors.body || 0x9AA0A6;
    box(.75, .85, .65, col, 0, .82, 0, g);
    box(.5, .45, .45, col, 0, 1.42, .28, g);
    sphere(.055, CHAR, -.14, 1.5, .5, g, 8); sphere(.055, CHAR, .14, 1.5, .5, g, 8);
    // apron
    box(.55, .65, .08, c.colors.apron || 0xD98C6A, 0, .8, .36, g);
    // cap
    cyl(.34, .36, .12, 0x5A4A4A, 0, 1.84, .02, g, 16);
    box(.4, .05, .32, 0x5A4A4A, 0, 1.78, .26, g);
    stickLegs(g, .42);
  },

  al: (c) => (g) => {
    const col = c.colors.body || 0x9AA0A6;
    box(.85, .95, .75, col, 0, .88, 0, g);
    box(.58, .52, .55, col, 0, 1.6, .3, g);
    sphere(.065, CHAR, -.17, 1.65, .34, g, 8);
    sphere(.065, CHAR,  .17, 1.65, .34, g, 8);
    // stubble
    for (let i = 0; i < 6; i++){
      const x = -.2 + i * .08;
      sphere(.025, 0x5A4A4A, x, 1.42, .56, g, 4);
    }
    // Elite Trucking cap
    cyl(.36, .38, .14, c.colors.cap || 0x3D5FC0, 0, 1.95, .02, g, 16);
    box(.42, .06, .3, c.colors.cap || 0x3D5FC0, 0, 1.88, .26, g);
    // plasma cannon (comic bluster)
    cyl(.08, .08, .55, 0xE8461E, .55, 1.05, .25, g, 8).rotation.z = -.4;
    box(.12, .08, .25, 0x444444, .35, 1.0, .25, g).rotation.z = -.25;
    stickLegs(g, .42);
  },

  garyvariants: (c) => (g) => {
    const variants = c.colors.variants || [0x8E5AC8, 0x5A78C8, 0x5AC87F];
    const main = variants[0];
    sphere(.28, main, 0, .75, 0, g, 14);
    cyl(.1, .12, .4, main, 0, .32, 0, g, 12);
    sphere(.09, EYE_W, -.1, .85, .2, g, 10);
    sphere(.09, EYE_W,  .1, .85, .2, g, 10);
    sphere(.04, CHAR, -.1, .85, .26, g, 8);
    sphere(.04, CHAR,  .1, .85, .26, g, 8);
    cyl(.015, .015, .2, main, -.08, 1.05, 0, g, 6);
    cyl(.015, .015, .2, main,  .08, 1.05, 0, g, 6);
    sphere(.035, variants[1], -.08, 1.18, 0, g, 8);
    sphere(.035, variants[2],  .08, 1.18, 0, g, 8);
    stickLegs(g, .4);
  },

  gokuguy: (c) => (g) => {
    cyl(.25, .25, .9, 0xE0B090, 0, .9, 0, g, 12);
    sphere(.28, 0xE0B090, 0, 1.45, 0, g, 14);
    sphere(.05, CHAR, -.1, 1.5, .22, g, 8);
    sphere(.05, CHAR,  .1, 1.5, .22, g, 8);
    // gi
    box(.55, .8, .35, c.colors.gi || 0xE08A1E, 0, .85, 0, g);
    box(.12, .7, .38, c.colors.accent || 0x5A78C8, 0, .85, 0, g);
    stickLegs(g, .5);
  },

  lobsterguy: (c) => (g) => {
    const col = c.colors.body || 0xE8461E;
    box(.7, .6, .9, col, 0, .75, 0, g);
    box(.5, .42, .45, col, 0, 1.25, .35, g);
    sphere(.06, CHAR, -.14, 1.35, .6, g, 8);
    sphere(.06, CHAR,  .14, 1.35, .6, g, 8);
    // claws
    cyl(.12, .08, .45, col, -.55, .95, .25, g, 8).rotation.z = .6;
    cyl(.12, .08, .45, col,  .55, .95, .25, g, 8).rotation.z = -.6;
    stickLegs(g, .42);
  },

  bigt: (c) => (g) => {
    const body = c.colors.body || 0xC97F86;
    box(.9, .8, .6, body, 0, .8, 0, g);
    box(.55, .45, .5, body, 0, 1.4, .25, g);
    // char edge
    box(.95, .12, .64, c.colors.edge || 0x5A2E1C, 0, .42, 0, g);
    sphere(.06, CHAR, -.16, 1.5, .5, g, 8);
    sphere(.06, CHAR,  .16, 1.5, .5, g, 8);
    // shades + cap
    box(.4, .1, .06, 0x111111, -.14, 1.52, .52, g);
    box(.4, .1, .06, 0x111111,  .14, 1.52, .52, g);
    cyl(.34, .38, .16, c.colors.cap || 0x35A7E0, 0, 1.86, .02, g, 16);
    box(.4, .06, .34, c.colors.cap || 0x35A7E0, 0, 1.78, .28, g);
    // chain
    const chain = torus(.32, .045, c.colors.chain || 0xF7C948, 0, .95, .32, g); chain.rotation.x = 1.25;
    box(.2, .16, .05, c.colors.chain || 0xF7C948, 0, .7, .5, g);
    // sauce bottle
    cyl(.08, .08, .28, c.colors.sauce || 0x9B2B1E, .55, .9, .2, g, 8).rotation.z = -.4;
    stickLegs(g, .42);
  }
};

export function characterBuilder(character){
  if (SPECIAL[character.id]) return SPECIAL[character.id](character);
  switch (character.kind){
    case 'bread': return buildBread(character);
    case 'fruit': return buildFruit(character);
    case 'food':  return buildFood(character);
    case 'creature': return buildCreature(character);
    case 'human': return buildHuman(character);
    default: return buildCreature(character);
  }
}

// ---- spawn helpers -------------------------------------------------------

function pickLine(character){
  const lines = character.lines || [];
  return lines.length ? lines[(Math.random() * lines.length) | 0] : '...';
}

export function spawnCharacter(character, opts = {}){
  const pos = opts.pos ? opts.pos.clone() : new THREE.Vector3(opts.x ?? 0, 0, opts.z ?? 0);
  const line = opts.line || pickLine(character);
  return new Townsperson({
    id: character.id,
    name: character.name,
    line,
    pos,
    facing: opts.facing ?? (Math.random() * Math.PI * 2),
    speed: opts.speed ?? (character.slow ? 0.45 : 1.0),
    wander: opts.wander ?? 0,
    roam: opts.roam ?? false,
    patrol: opts.patrol ?? false,
    schedule: opts.schedule ?? 'always',
    bubbleY: opts.bubbleY ?? 2.4,
    giver: opts.giver ?? null,
    quadruped: character.quadruped || false,
    slow: character.slow || false,
    // opts.build lets a scene keep a bespoke mesh while still taking its identity
    // (id, name, lines) from the roster — the roster rule is about DATA, not geometry.
    build: opts.build || characterBuilder(character)
  });
}

// ---- town square ---------------------------------------------------------
// Named townsfolk from the roster, each with a signature spot + behavior.

const TOWN_BEHAVIOR = {
  sheriffberry: { x: 5.5, z: 9.5, facing: Math.PI, patrol: true, speed: 1.1, bubbleY: 2.6 },
  bullnana:     { x: -7.5, z: 9.5, roam: true, speed: 1.0, bubbleY: 2.2 },
  snailman:     { x: 9.5, z: 6.5, roam: true, speed: 0.4, bubbleY: 1.8 },
  footman:      { x: -9.5, z: -2, facing: Math.PI / 2, roam: true, speed: 1.35, bubbleY: 2.2 },
  crsr:         { x: 9.5, z: 1.5, facing: -Math.PI / 2, roam: true, speed: 1.15, bubbleY: 3.0, schedule: 'night' },
  kingbread:    { x: 7.5, z: 0, facing: -Math.PI / 2, speed: 0, bubbleY: 2.6, schedule: 'special' },
  beatnik:      { x: -7.5, z: 6.5, wander: 3.2, speed: 0.8, bubbleY: 2.4 },
  coffeerocker: { x: 8, z: -5, wander: 3.0, speed: 0.7, bubbleY: 2.4 },
  bananabunch:  { x: -4, z: 8, roam: true, speed: 0.9, bubbleY: 2.2 }
};

export function makeTownFolk(){
  const list = townsfolk();
  return list.map(c => {
    const behavior = TOWN_BEHAVIOR[c.id] || { x: (Math.random() * 10 - 5), z: (Math.random() * 10 - 5), wander: 2 };
    return spawnCharacter(c, behavior);
  });
}

// ---- downtown crowd ------------------------------------------------------
// A mix of named roster customers/townsfolk + generic generated toastfolk.

const CRUSTS   = [C.crust, C.toast, C.rye, 0xD9A441, 0xC9A876, 0xCF8F4E];
const HATCOLS  = [C.cobalt, C.raspberry, C.sprout, C.tangerine, 0x1FB6A6];
const GENERIC_LINES = [
  'fresh out the oven!', 'lovely day downtown.', 'just browsing the shops.',
  'have you tried the café?', 'mmm, smells like toast.', 'nice and toasty today.',
  'new fit? lookin\' sharp.', 'crumbs well spent, i say.', 'busy little town, huh?',
  'mornin\', neighbor.'
];

function genericToastfolk(){
  const crust = CRUSTS[(Math.random() * CRUSTS.length) | 0];
  const hatRoll = Math.random();
  const hat = hatRoll < 0.28 ? 'cap' : hatRoll < 0.45 ? 'beret' : null;
  const hatColor = HATCOLS[(Math.random() * HATCOLS.length) | 0];
  const name = 'Toastfolk';
  const line = GENERIC_LINES[(Math.random() * GENERIC_LINES.length) | 0];
  const build = (g) => {
    breadSlice(g, crust, C.cream, 0.58, 1.05);
    sphere(.06, CHAR, -.15, 1.12, .3, g, 8);
    sphere(.06, CHAR,  .15, 1.12, .3, g, 8);
    if (hat === 'cap'){
      const cap = new THREE.Group(); cap.position.set(0, 1.64, 0); g.add(cap);
      cyl(.34, .37, .24, hatColor, 0, 0, 0, cap, 14);
      box(.42, .05, .3, hatColor, 0, -.08, .28, cap);
    } else if (hat === 'beret'){
      cyl(.35, .37, .12, hatColor, 0, 1.6, .02, g, 16);
      cyl(.04, .04, .07, C.crustDark, 0, 1.68, .02, g, 6);
    }
    stickLegs(g, .48);
  };
  return { name, line, build };
}

export function makeDowntownCrowd(count = 7){
  const folk = [];
  const pool = spawnable().filter(c =>
    (c.role === 'customer' || c.role === 'townsfolk') &&
    !c.building && c.id !== 'kingbread' && c.id !== 'crsr'
  );
  // scatter a few named faces
  const named = pool.slice(0, Math.min(3, pool.length));
  const usedXs = [];
  named.forEach((c, i) => {
    const x = -7 + i * 7;
    const z = 0.8 + Math.random() * 3.5;
    usedXs.push(x);
    folk.push(spawnCharacter(c, { x, z, wander: 1.2 + Math.random(), speed: 0.7 + Math.random() * 0.4, bubbleY: 2.2 }));
  });
  // fill remaining slots with generic toastfolk
  for (let i = named.length; i < count; i++){
    const g = genericToastfolk();
    const x = -9 + (i + 0.5) * (18 / count) + (Math.random() * 2 - 1);
    const z = 0.5 + Math.random() * 4.5;
    folk.push(new Townsperson({
      name: g.name, line: g.line,
      pos: new THREE.Vector3(x, 0, z),
      facing: Math.random() * Math.PI * 2,
      wander: 1.4 + Math.random() * 1.2,
      speed: 0.8 + Math.random() * 0.5,
      bubbleY: 2.2,
      build: g.build
    }));
  }
  return folk;
}

// ---- shopkeepers / quest givers ------------------------------------------

export function makeShopkeeper(buildingId, pos, opts = {}){
  const c = shopkeeper(buildingId);
  if (!c) return null;
  return spawnCharacter(c, { pos, ...opts });
}

export function makeCustomer(pos, opts = {}){
  const list = customers();
  if (!list.length) return null;
  const c = list[(Math.random() * list.length) | 0];
  return spawnCharacter(c, { pos, ...opts });
}
